import React from 'react';
import AppListToolbarGlobal, { type CompanyOption } from '@/__core/components/AppLisstToolbarGlobal';
import {
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  DeleteOutline as TrashIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
} from '@mui/icons-material';
import Badge from '@mui/material/Badge';
import { sharedToolbarListStyles } from '@/__core/styles';

export interface EmployeesToolbarGlobalProps {
  /** @deprecated Chỉ dùng khi test; mặc định lấy từ auth + resolveModuleAccessibleCompanies */
  companies?: CompanyOption[];
  fetchCompanies?: () => Promise<CompanyOption[]>;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  onSearchSubmit?: () => void;
  onToggleAdvancedSearch?: () => void;
  isAdvancedSearchOpen?: boolean;
  onFilterChange?: (field: string, value: string) => void;

  filters?: {
    company: string;
    department: string;
    role: string;
    status: string;
  };
  departments?: Array<{ id: string; name: string }>;
  roles?: Array<{ id: string; display_name: string }>;
  selectedCount?: number;
  onDelete?: () => void;
  onExport?: () => void;
  canExport?: boolean;
  onImport?: () => void;
  canImport?: boolean;
  onOpenTrash?: () => void;
  deletedItemsCount?: number;
  canDelete?: boolean;
}

export default function EmployeesToolbarGlobal({
  companies,
  searchQuery = '',
  onSearchChange,
  onSearchSubmit,
  onToggleAdvancedSearch,
  isAdvancedSearchOpen = false,
  filters = { company: 'all', department: 'all', role: 'all', status: 'all' },
  onFilterChange,
  departments = [],
  roles = [],
  selectedCount = 0,
  onDelete,
  onExport,
  canExport = true,
  onImport,
  canImport = true,
  onOpenTrash,
  deletedItemsCount = 0,
  canDelete = true,
}: EmployeesToolbarGlobalProps) {
  const statusOptions = React.useMemo(
    () => [
      { value: 'all', label: 'Tất cả' },
      { value: 'active', label: 'Hoạt động' },
      { value: 'inactive', label: 'Không hoạt động' },
      { value: 'on_leave', label: 'Nghỉ phép' },
      { value: 'terminated', label: 'Nghỉ việc' },
    ],
    []
  );

  const departmentOptions = React.useMemo(
    () => [
      { value: 'all', label: 'Tất cả' },
      ...departments.map((d) => ({ value: d.id, label: d.name })),
    ],
    [departments]
  );

  const roleOptions = React.useMemo(
    () => [
      { value: 'all', label: 'Tất cả' },
      ...roles.map((r) => ({ value: r.id, label: r.display_name })),
    ],
    [roles]
  );

  const advancedFilterFields = React.useMemo(
    () => [
      {
        key: 'department',
        label: 'Phòng ban',
        labelWidth: 80,
        value: filters.department,
        placeholder: 'Chọn phòng ban',
        options: departmentOptions,
        onChange: (value: string) => onFilterChange?.('department', value),
        gridSize: { xs: 12, sm: 6, md: 6 },
      },
      {
        key: 'role',
        label: 'Vai trò',
        labelWidth: 60,
        value: filters.role,
        placeholder: 'Chọn vai trò',
        options: roleOptions,
        onChange: (value: string) => onFilterChange?.('role', value),
        gridSize: { xs: 12, sm: 6, md: 6 },
      },
      {
        key: 'status',
        label: 'Trạng thái',
        labelWidth: 70,
        value: filters.status,
        placeholder: 'Chọn trạng thái',
        options: statusOptions,
        onChange: (value: string) => onFilterChange?.('status', value),
        gridSize: { xs: 12, sm: 6, md: 6 },
      },
    ],
    [
      departmentOptions,
      filters.department,
      filters.role,
      filters.status,
      onFilterChange,
      roleOptions,
      statusOptions,
    ]
  );

  const rightActions = (
    <Box
      className='employee-toolbar-action-buttons'
      sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
    >
      {canImport && onImport && (
        <Tooltip title='Import' arrow>
          <IconButton
            onClick={onImport}
            size='small'
            sx={sharedToolbarListStyles.orderActionIconButton}
          >
            <ImportIcon sx={sharedToolbarListStyles.orderToolbarActionIcon} />
          </IconButton>
        </Tooltip>
      )}
      {canExport && onExport && (
        <Tooltip title='Export' arrow>
          <IconButton
            onClick={onExport}
            size='small'
            sx={sharedToolbarListStyles.orderActionIconButton}
          >
            <ExportIcon sx={sharedToolbarListStyles.orderToolbarActionIcon} />
          </IconButton>
        </Tooltip>
      )}
      {onOpenTrash && (
        <Tooltip title='Thùng rác' arrow>
          <IconButton
            onClick={onOpenTrash}
            size='small'
            sx={{
              ...sharedToolbarListStyles.actionIconButtonBase,
              ...sharedToolbarListStyles.trashIconButton,
            }}
          >
            <Badge badgeContent={deletedItemsCount} color='error' max={120}>
              <TrashIcon sx={sharedToolbarListStyles.toolbarActionIcon} />
            </Badge>
          </IconButton>
        </Tooltip>
      )}
      {selectedCount > 0 && canDelete && onDelete && (
        <Tooltip title={`Xóa (${selectedCount})`} arrow>
          <IconButton
            onClick={onDelete}
            size='small'
            sx={{
              ...sharedToolbarListStyles.actionIconButtonBase,
              ...sharedToolbarListStyles.deleteIconButton,
            }}
          >
            <Badge badgeContent={selectedCount} color='error' max={99}>
              <DeleteIcon sx={sharedToolbarListStyles.toolbarActionIcon} />
            </Badge>
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <AppListToolbarGlobal
      scopeModule='employees'
      companies={companies}
      searchPlaceholder='Tìm kiếm nhân viên...'
      searchQuery={searchQuery}
      onSearchChange={onSearchChange}
      onSearchSubmit={onSearchSubmit}
      isAdvancedSearchOpen={isAdvancedSearchOpen}
      onToggleAdvancedSearch={onToggleAdvancedSearch}
      selectedCompany={filters.company}
      onCompanyChange={(id) => onFilterChange?.('company', id)}
      advancedFilterFields={advancedFilterFields}
      rightActions={rightActions}
    />
  );
}
